/* eslint-disable class-methods-use-this */

import { getIsPosthogEnabled, posthog } from 'boot/posthog';
import { auth as useAuthStore } from 'src/store/auth';
import { resetFeatureFlagsState } from 'src/composables/analytics/useFeatureFlags';

/** @typedef {import('./analyticsTypes').CommonEventProps} CommonEventProps */

const PERSON_PROPS_FINGERPRINT_KEY = 'posthogPersonPropsFingerprint';

/**
 * Build a stable fingerprint for the person properties sent to PostHog.
 *
 * @param {CommonEventProps} props
 * @returns {string}
 */
function getPersonPropsFingerprint(props) {
  return JSON.stringify(Object.keys(props).sort().map((key) => [key, props[key]]));
}

/**
 * Read the fingerprint of the person properties last sent to PostHog.
 *
 * @returns {string|null}
 */
function readPersonPropsFingerprint() {
  try {
    return localStorage.getItem(PERSON_PROPS_FINGERPRINT_KEY);
  } catch {
    return null;
  }
}

/**
 * Persist the fingerprint of the person properties sent to PostHog.
 *
 * @param {string|null} fingerprint - Pass null to forget the previously sent properties.
 * @returns {void}
 */
function writePersonPropsFingerprint(fingerprint) {
  try {
    if (fingerprint === null) {
      localStorage.removeItem(PERSON_PROPS_FINGERPRINT_KEY);
      return;
    }

    localStorage.setItem(PERSON_PROPS_FINGERPRINT_KEY, fingerprint);
  } catch {
    // Do nothing
  }
}

export default class CoreAnalytics {
  /**
   * Get common properties for analytics events, such as user ID, clinic ID, and role.
   *
   * @returns {CommonEventProps} Common properties for analytics events.
   */
  getCommonProps() {
    const { userDetails, connectedClinic } = useAuthStore();

    return {
      userId: userDetails?.userId || null,
      isAdmin: userDetails?.admin || false,
      clinicId: userDetails?.clinic || null,
      role: userDetails?.role || null,
      integration: connectedClinic?.integration || 'none',
    };
  }

  /**
   * Identify the current user in PostHog and load feature flags.
   *
   * @returns {void}
   */
  identifyUser() {
    if (!getIsPosthogEnabled()) {
      return;
    }

    try {
      const commonProps = this.getCommonProps();

      if (!commonProps.userId) {
        return;
      }

      // Passing person properties makes posthog-js schedule an extra `/flags` request, so they are
      // only sent when they actually changed since the last identify. They are still attached to
      // every event through `register_for_session` and `track` below.
      const fingerprint = getPersonPropsFingerprint(commonProps);

      if (readPersonPropsFingerprint() === fingerprint) {
        posthog.identify(commonProps.userId);
      } else {
        posthog.identify(commonProps.userId, commonProps);
        writePersonPropsFingerprint(fingerprint);
      }

      // Share common props for autocapture events
      posthog.register_for_session(commonProps);
      // `advanced_disable_feature_flags_on_first_load` keeps PostHog from evaluating flags on boot,
      // so this is the single request that loads them.
      posthog.reloadFeatureFlags();
    } catch (error) {
      if (process.env.ENV_TYPE !== 'production') {
        console.error(error);
      }
    }
  }

  /**
   * Reset the current PostHog user session.
   *
   * @returns {void}
   */
  resetUser() {
    if (!getIsPosthogEnabled()) {
      return;
    }

    try {
      posthog.reset();
      writePersonPropsFingerprint(null);
      resetFeatureFlagsState();
    } catch (error) {
      if (process.env.ENV_TYPE !== 'production') {
        console.error(error);
      }
    }
  }

  /**
   * Record the provided event to the analytic service.
   *
   * @param {string} eventName
   * @param {Object} [props]
   * @throws {never}
   */
  track(eventName, props = {}) {
    if (!getIsPosthogEnabled()) {
      return;
    }

    try {
      posthog.capture(eventName, {
        ...this.getCommonProps(),
        ...props,
      });
    } catch (error) {
      // Ignore analytics errors to prevent them from affecting the user experience.
      if (process.env.ENV_TYPE !== 'production') {
        console.error(error);
      }
    }
  }
}
